export type Translate<TKey extends string> = (key: TKey, values?: Record<string, unknown>) => string;

const formatValue = (value: unknown, format: string | undefined) => {
  if (format === 'list' && Array.isArray(value)) {
    return new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(value.map(String));
  }
  if (format === 'number' && typeof value === 'number') return new Intl.NumberFormat('en').format(value);
  return String(value ?? '');
};

/**
 * Translates from a fixed set of English texts the way i18next reads them, for a component whose host passes no
 * translation: a `count` picks the `_one` or `_other` text when there is one, and `{{name, list}}` and
 * `{{name, number}}` format their values.
 */
export const createTranslate =
  <TKey extends string>(texts: Record<TKey, string>): Translate<TKey> =>
  (key, values = {}) => {
    const textsByKey: Partial<Record<string, string>> = texts;
    const { count } = values;
    const pluralKey = typeof count === 'number' ? `${key}_${count === 1 ? 'one' : 'other'}` : key;
    const template = textsByKey[pluralKey] ?? textsByKey[key] ?? key;

    return template.replace(/\{\{-?\s*(\w+)(?:,\s*(\w+))?\s*\}\}/g, (_match, name: string, format?: string) =>
      formatValue(values[name], format),
    );
  };
